// Time Furniture CRM — media-capable WhatsApp message components.
import React, { useEffect, useState } from "react";
import {
  Download, X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, MapPin, FileText,
  ExternalLink, RefreshCw, Mic, Film, ImageOff,
} from "lucide-react";
import { crmApi } from "../../lib/crmApi";

export function useMediaUrl(mediaId) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let live = true;
    if (!mediaId) return undefined;
    crmApi.fetchMediaUrl(mediaId).then((u) => live && setUrl(u)).catch(() => live && setErr(true));
    return () => { live = false; };
  }, [mediaId]);
  return { url, err };
}

const fmtSize = (b) => {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};

function downloadUrl(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function MediaFailed({ c }) {
  return (
    <p className="text-[10px] text-[#A3141A] flex items-center gap-1 mt-1" data-testid="wa-media-failed">
      <ImageOff size={11} /> Media not stored: {c.media_error || "download failed"} — original text: {c.message_text}
    </p>
  );
}

// ------------------------------------------------------------ images
export function ImageThumb({ c, onOpen }) {
  const { url, err } = useMediaUrl(c.media_id);
  if (err) return <MediaFailed c={c} />;
  if (!url) return <div className="w-36 h-28 bg-[#6E1F2C]/8 animate-pulse" />;
  return (
    <button type="button" onClick={onOpen} className="block group relative" data-testid="wa-image-thumb">
      <img src={url} alt={c.media_meta?.filename || "image"} loading="lazy"
        className="max-h-52 max-w-full object-cover border border-[#6E1F2C]/15 group-hover:opacity-90" />
      <span className="absolute bottom-1 right-1 bg-black/50 text-white p-0.5 opacity-0 group-hover:opacity-100"><ZoomIn size={12} /></span>
    </button>
  );
}

export function ImageGrid({ items, onOpen }) {
  const cols = items.length === 1 ? "grid-cols-1" : "grid-cols-2";
  return (
    <div className={`grid ${cols} gap-1`} data-testid="wa-image-grid">
      {items.map((c, i) => <ImageThumb key={c.id} c={c} onOpen={() => onOpen(c)} />)}
    </div>
  );
}

export function Lightbox({ images, index, setIndex, onClose }) {
  const c = images[index];
  const { url } = useMediaUrl(c?.media_id);
  const [zoom, setZoom] = useState(false);
  useEffect(() => { setZoom(false); }, [index]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && index > 0) setIndex(index - 1);
      if (e.key === "ArrowRight" && index < images.length - 1) setIndex(index + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, images.length, onClose, setIndex]);
  if (!c) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/90 flex flex-col" data-testid="wa-lightbox">
      <div className="flex items-center justify-between px-4 py-2 text-white/80 text-[11px]">
        <span>{c.media_meta?.filename} · {fmtSize(c.media_meta?.size)} · {index + 1}/{images.length}</span>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => setZoom(!zoom)} title="Zoom" data-testid="wa-lightbox-zoom" className="hover:text-white">
            {zoom ? <ZoomOut size={16} /> : <ZoomIn size={16} />}
          </button>
          <button type="button" onClick={() => url && downloadUrl(url, c.media_meta?.filename)} title="Download" data-testid="wa-lightbox-download" className="hover:text-white"><Download size={16} /></button>
          <button type="button" onClick={() => url && window.open(url, "_blank")} title="Open in new tab" className="hover:text-white"><ExternalLink size={16} /></button>
          <button type="button" onClick={onClose} title="Close" data-testid="wa-lightbox-close" className="hover:text-white"><X size={18} /></button>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center overflow-auto relative px-12">
        {index > 0 && (
          <button type="button" onClick={() => setIndex(index - 1)} data-testid="wa-lightbox-prev"
            className="absolute left-2 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-white/10 p-2"><ChevronLeft size={22} /></button>
        )}
        {url ? (
          <img src={url} alt="" onClick={() => setZoom(!zoom)}
            className={zoom ? "cursor-zoom-out max-w-none" : "cursor-zoom-in max-h-full max-w-full object-contain"} />
        ) : <p className="text-white/60 text-xs">Loading…</p>}
        {index < images.length - 1 && (
          <button type="button" onClick={() => setIndex(index + 1)} data-testid="wa-lightbox-next"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-white/10 p-2"><ChevronRight size={22} /></button>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------ audio
export function AudioMessage({ c, onRetry }) {
  const { url, err } = useMediaUrl(c.media_id);
  return (
    <div data-testid="wa-audio-message">
      <p className="text-[10px] text-[#7A6B5F] flex items-center gap-1 mb-1"><Mic size={11} /> Voice note
        {c.media_meta?.duration ? ` · ${Math.round(c.media_meta.duration)}s` : ""}</p>
      {err && <MediaFailed c={c} />}
      {url && (
        <div className="flex items-center gap-1.5">
          <audio controls src={url} preload="metadata" className="h-9 max-w-[240px]" data-testid="wa-audio-player" />
          <button type="button" title="Download" onClick={() => downloadUrl(url, c.media_meta?.filename)}
            className="text-[#6E1F2C] hover:opacity-70"><Download size={14} /></button>
        </div>
      )}
      {!url && !err && <p className="text-[10px] text-[#7A6B5F] italic">Loading audio…</p>}
      {c.transcription_status === "pending" && <p className="text-[10px] text-[#A6822F] mt-1 italic" data-testid="wa-transcript-pending">Transcribing…</p>}
      {c.transcription_status === "done" && c.transcript && (
        <p className="text-[12px] text-[#1C110C] mt-1 border-l-2 border-[#D4A963] pl-2" data-testid="wa-transcript">
          {c.transcript}
          {c.transcript_language && <span className="text-[9px] text-[#7A6B5F] ml-1 uppercase">({c.transcript_language})</span>}
        </p>
      )}
      {c.transcription_status === "done" && !c.transcript && <p className="text-[10px] text-[#7A6B5F] mt-1 italic">No speech detected.</p>}
      {c.transcription_status === "failed" && (
        <p className="text-[10px] text-[#A3141A] mt-1 flex items-center gap-2" data-testid="wa-transcript-failed">
          Transcription failed: {c.transcription_error || "unknown error"}
          <button type="button" onClick={onRetry} data-testid="wa-transcript-retry"
            className="inline-flex items-center gap-1 border border-[#A3141A]/40 px-1.5 py-0.5 hover:bg-[#A3141A]/10"><RefreshCw size={10} /> Retry</button>
        </p>
      )}
    </div>
  );
}

// ------------------------------------------------------------ video / documents / location
export function VideoMessage({ c }) {
  const { url, err } = useMediaUrl(c.media_id);
  if (err) return <MediaFailed c={c} />;
  return (
    <div data-testid="wa-video-message">
      <p className="text-[10px] text-[#7A6B5F] flex items-center gap-1 mb-1"><Film size={11} /> Video · {fmtSize(c.media_meta?.size)}</p>
      {url ? (
        <div>
          <video controls src={url} className="max-h-64 max-w-full border border-[#6E1F2C]/15" data-testid="wa-video-player" />
          <button type="button" onClick={() => downloadUrl(url, c.media_meta?.filename)}
            className="text-[10px] text-[#6E1F2C] hover:underline inline-flex items-center gap-1 mt-1"><Download size={11} /> Download</button>
        </div>
      ) : <p className="text-[10px] text-[#7A6B5F] italic">Loading video…</p>}
    </div>
  );
}

export function DocMessage({ c }) {
  const { url, err } = useMediaUrl(c.media_id);
  const [preview, setPreview] = useState(false);
  const meta = c.media_meta || {};
  const isPdf = (meta.mime_type || "").includes("pdf");
  if (err) return <MediaFailed c={c} />;
  return (
    <div data-testid="wa-doc-message">
      <div className="flex items-center gap-2 border border-[#6E1F2C]/15 bg-white/60 px-2.5 py-2">
        <FileText size={18} className="text-[#6E1F2C] shrink-0" />
        <div className="min-w-0">
          <p className="text-[12px] text-[#1C110C] truncate" data-testid="wa-doc-filename">{meta.filename || "document"}</p>
          <p className="text-[9px] text-[#7A6B5F] uppercase">{(meta.mime_type || "").split("/").pop()} · {fmtSize(meta.size)}</p>
        </div>
        <div className="ml-auto flex items-center gap-1.5 shrink-0">
          {isPdf && url && (
            <button type="button" onClick={() => setPreview(true)} data-testid="wa-doc-preview-btn"
              className="text-[9px] uppercase tracking-[0.1em] border border-[#6E1F2C]/30 text-[#6E1F2C] px-1.5 py-1 hover:bg-[#6E1F2C]/10">Preview</button>
          )}
          {url && <button type="button" title="Download" onClick={() => downloadUrl(url, meta.filename)} data-testid="wa-doc-download"
            className="text-[#6E1F2C] hover:opacity-70"><Download size={14} /></button>}
        </div>
      </div>
      {preview && url && (
        <div className="fixed inset-0 z-[80] bg-black/85 flex flex-col" data-testid="wa-pdf-viewer">
          <div className="flex items-center justify-between px-4 py-2 text-white/80 text-[11px]">
            <span>{meta.filename}</span>
            <button type="button" onClick={() => setPreview(false)} className="hover:text-white" data-testid="wa-pdf-close"><X size={18} /></button>
          </div>
          <iframe title={meta.filename} src={url} className="flex-1 bg-white mx-auto w-full max-w-4xl" />
        </div>
      )}
    </div>
  );
}

export function LocationMessage({ c }) {
  const ld = c.location_data || {};
  let lat = ld.latitude, lng = ld.longitude;
  if (lat == null) {
    const m = (c.message_text || "").match(/\((-?\d+\.?\d*),\s*(-?\d+\.?\d*)\)/);
    if (m) { lat = m[1]; lng = m[2]; }
  }
  return (
    <div data-testid="wa-location-message">
      <p className="text-[12px] text-[#1C110C] flex items-center gap-1"><MapPin size={12} className="text-[#A3141A]" />
        {ld.name || ld.address || "Shared location"}{lat != null && <span className="text-[#7A6B5F] text-[10px]">({lat}, {lng})</span>}</p>
      {lat != null && (
        <>
          <iframe title="map" src={`https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`}
            className="w-64 h-36 border border-[#6E1F2C]/15 mt-1" loading="lazy" />
          <a href={`https://maps.google.com/?q=${lat},${lng}`} target="_blank" rel="noreferrer"
            className="text-[10px] text-[#6E1F2C] hover:underline inline-flex items-center gap-1 mt-0.5" data-testid="wa-location-open">
            <ExternalLink size={10} /> Open in Google Maps
          </a>
        </>
      )}
    </div>
  );
}

// dispatcher for one non-image media message
export function MediaBody({ c, onRetryTranscription }) {
  if (c.message_type === "location") return <LocationMessage c={c} />;
  if (!c.media_id) {
    if (c.media_status === "failed") return <MediaFailed c={c} />;
    return null; // legacy message — plain text fallback rendered by caller
  }
  if (c.message_type === "audio") return <AudioMessage c={c} onRetry={onRetryTranscription} />;
  if (c.message_type === "video") return <VideoMessage c={c} />;
  if (c.message_type === "document" || c.message_type === "sticker") return <DocMessage c={c} />;
  return null;
}
